import { BigNumber } from '@ethersproject/bignumber';
import { ONE_HUNDRED_PERCENT } from 'app/constants';
import { map } from 'fp-ts/lib/Array';
import { flow } from 'fp-ts/lib/function';
import { negate } from 'lodash';
import { DecoratedSilo, SiloAsset } from './types';
import { isDisabledCollateral } from '../../features/silo/disabled-collateral/isDisabledCollateral';
import { and } from 'fp-ts/lib/Predicate';
import { SiloConfiguration } from '@silo/config-legacy';
import { getIsCurveLpAsset } from '../../features/util/curve-lp';
import { CollateralType, PERCENT_DECIMALS } from '@silo/types';
import { disjoin, or, shiftDecimal } from '@silo/utils';

// utilities
type DecoratedSiloUtility<T> = (decoratedSilo: DecoratedSilo) => T;

export const getUserHasLoan: DecoratedSiloUtility<boolean> = ({ userLTV }) => !userLTV.isZero();

export const getDecoratedSiloHealthFactor: DecoratedSiloUtility<bigint> = (decoratedSilo) =>
  getHealthFactor({
    userLTV: decoratedSilo.userLTV,
    userLiquidationThreshold: decoratedSilo.userLiquidationThreshold,
  });

const getHealthFactor = ({
  userLTV: userLTVBn,
  userLiquidationThreshold: userLiquidationThresholdBn,
}: {
  userLiquidationThreshold: BigNumber;
  userLTV: BigNumber;
}) => {
  const userLiquidationThreshold = userLiquidationThresholdBn.toBigInt();
  const userLTV = userLTVBn.toBigInt();

  if (userLiquidationThreshold === 0n) return 0n;

  const difference = userLiquidationThreshold - userLTV;

  return shiftDecimal(difference, PERCENT_DECIMALS) / userLiquidationThreshold;
};

export const getMaxLiquidationFee: DecoratedSiloUtility<BigNumber> = ({ userLiquidationThreshold }) => {
  return ONE_HUNDRED_PERCENT.sub(userLiquidationThreshold);
};

export const getDecoratedSiloBorrowingPowerUsed: DecoratedSiloUtility<bigint> = (decoratedSilo) => {
  const userMaximumLTV = decoratedSilo.userMaximumLTV.toBigInt();
  const userLTV = decoratedSilo.userLTV.toBigInt();

  if (userMaximumLTV === 0n) return 0n;

  const borrowedPercent = shiftDecimal(userLTV, PERCENT_DECIMALS) / userMaximumLTV;

  return borrowedPercent;
};

export const getBorrowableAssets =
  (siloConfig: SiloConfiguration): DecoratedSiloUtility<SiloAsset[]> =>
  ({ assets }) =>
    assets.filter(getIsBorrowableAsset(siloConfig));

export const getCollateralAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) =>
  assets.filter(getHasEffectiveCollateral);

export const getAvailableCollateralAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) =>
  assets.filter(getIsAvailableCollateral);

export const getUserHasCollateral: DecoratedSiloUtility<boolean> = ({ assets }) =>
  assets.some(getHasEffectiveCollateral);

export const getNonCollateralAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) =>
  assets.filter(getIsNonCollateral);

export const getDebtAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) => assets.filter(getHasDebt);
export const getUserHasDebt: DecoratedSiloUtility<boolean> = ({ assets }) => assets.some(getHasDebt);
export const getNonDebtAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) => assets.filter(negate(getHasDebt));
export const getLoanAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) => assets.filter(getIsLoanAsset);

export const getAreAllAssetsCollateral: DecoratedSiloUtility<boolean> = ({ assets }) =>
  assets.every(getHasEffectiveCollateral);

export const getBaseAsset: DecoratedSiloUtility<SiloAsset> = ({ assets }) => assets[0];
export const getBridgeAssets: DecoratedSiloUtility<SiloAsset[]> = ({ assets }) => assets.slice(1);

// asset utilities
type SiloAssetsUtility<T> = (siloAssets: SiloAsset[]) => T;
type SiloAssetUtility<T> = (siloAsset: SiloAsset) => T;

export const getAssetsHash: SiloAssetsUtility<string> = (assets) =>
  assets.map(({ assetAddress }) => assetAddress).join('-');

const getSymbol: SiloAssetUtility<string> = ({ symbol }) => symbol;

export const disjoinSymbols = flow(map(getSymbol), disjoin);

/**
 * TODO: add `"protected-only"` here? although protected-only is not enforced on the contracts side
 */
export const getIsBorrowableAsset =
  (siloConfig: SiloConfiguration): SiloAssetUtility<boolean> =>
  ({ assetKey }) =>
    !getIsCurveLpAsset(siloConfig, assetKey);

const getIsCollateralAsset: SiloAssetUtility<boolean> = ({ collateralBalance }) => !collateralBalance.isZero();

export const getIsCollateralOnlyAsset: SiloAssetUtility<boolean> = ({ collateralOnlyBalance }) =>
  !collateralOnlyBalance.isZero();

export const getHasEffectiveCollateral: SiloAssetUtility<boolean> = ({ collateralBalance, collateralOnlyBalance }) =>
  !collateralBalance.isZero() || !collateralOnlyBalance.isZero();

export const getEffectiveCollateral: SiloAssetUtility<BigNumber> = ({ collateralBalance, collateralOnlyBalance }) =>
  collateralBalance.add(collateralOnlyBalance);

export const getHasDebt: SiloAssetUtility<boolean> = ({ debtBalance }) => !debtBalance.isZero();

export const getIsLoanAsset = or(getHasEffectiveCollateral, getHasDebt);

export const getCollateralType: SiloAssetUtility<CollateralType> = (asset) => {
  const hasBorrowableCollateral = getIsCollateralAsset(asset);
  const hasProtectedCollateral = getIsCollateralOnlyAsset(asset);

  switch (true) {
    case hasBorrowableCollateral && hasProtectedCollateral:
      return 'dual';
    case hasBorrowableCollateral:
      return 'borrowable';
    case hasProtectedCollateral:
      return 'protected';
    default:
      return 'none';
  }
};

/**
 * Returns a combined deposit for a given asset, by adding up the total deposits, with collateral only deposits
 */
export const getCombinedDeposits: SiloAssetUtility<BigNumber> = ({ totalDeposits, collateralOnlyDeposits }) =>
  totalDeposits.add(collateralOnlyDeposits);

export const getAssetMaxLiquidationFee: SiloAssetUtility<BigNumber> = ({ liquidationThreshold }) =>
  ONE_HUNDRED_PERCENT.sub(liquidationThreshold);

export const getIsNonCollateral = negate(getHasEffectiveCollateral);

const getIsAvailableCollateral: SiloAssetUtility<boolean> = and(getIsNonCollateral)(
  ({ maximumLTV }) => !isDisabledCollateral(maximumLTV.toBigInt())
);
